import React, { useMemo, useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography, CircularProgress } from "@mui/material";
import ReactEcharts from "echarts-for-react";
import moment from "moment";
import Section from "../../../../common/components/Dashboard/Section";
import { useDashboardTrackingHistory } from "../../../../common/API/hooks";
import { useSelectorValue } from "../../../customDashboards/common/providers/selectorProvider";
import { formatDateToObjectFromAndTo, aggregateByDate } from "../utils";

// Fetch up to 2000 records to cover full date range for the chart
const CHART_PAGE_SIZE = 2000;

/** Returns ECharts markArea data pairs for each contiguous weekend block in dates[]. */
function getWeekendBands(dates) {
    const bands = [];
    let bandStart = null;

    dates.forEach((date, i) => {
        const dow = moment(date).day(); // 0=Sun, 6=Sat
        const isWeekend = dow === 0 || dow === 6;
        if (isWeekend && bandStart === null) {
            bandStart = date;
        } else if (!isWeekend && bandStart !== null) {
            bands.push([{ xAxis: bandStart }, { xAxis: dates[i - 1] }]);
            bandStart = null;
        }
    });
    if (bandStart !== null) {
        bands.push([{ xAxis: bandStart }, { xAxis: dates[dates.length - 1] }]);
    }
    return bands;
}

export default function ActivityTimeline({ username }) {
    const date = useSelectorValue("date");
    const dateTo = useSelectorValue("dateTo");
    const module = useSelectorValue("module");
    const dateRange = formatDateToObjectFromAndTo(date, dateTo);

    const [metric, setMetric] = useState("totalLoads");

    const filters = useMemo(
        () => ({
            ...dateRange,
            username: username || null,
            module_name: module === "All" ? null : module,
            page: 1,
            page_size: CHART_PAGE_SIZE,
            sort_by: "datetime",
            sort_order: "asc",
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(dateRange), username, module]
    );

    const { data, isLoading, error } = useDashboardTrackingHistory(filters);
    const results = data?.results ?? [];

    // Pass selector date range so zero-filled days span the full chosen window
    const dailyData = useMemo(
        () => aggregateByDate(results, date, dateTo),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [results, date, dateTo]
    );

    const dates = dailyData.map((d) => d.date);
    const values = dailyData.map((d) => d[metric]);
    const weekendBands = useMemo(() => getWeekendBands(dates), [dates]);

    const option = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            formatter: (params) => {
                const p = params[0];
                const dow = moment(p.axisValue).format("ddd");
                const label = metric === "totalLoads" ? "Page loads" : "Distinct dashboards";
                return `<strong>${p.axisValue}</strong> (${dow})<br/>${label}: <strong>${p.value}</strong>`;
            },
        },
        grid: { containLabel: true, top: "10%", left: "3%", right: "3%", bottom: "3%" },
        xAxis: {
            type: "category",
            data: dates,
            axisLabel: { rotate: dates.length > 14 ? 30 : 0 },
        },
        yAxis: {
            type: "value",
            minInterval: 1,
            name: metric === "totalLoads" ? "Loads" : "Dashboards",
        },
        series: [
            {
                type: "line",
                data: values,
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                lineStyle: { color: "#1976d2", width: 2 },
                itemStyle: { color: "#1976d2" },
                areaStyle: {
                    color: {
                        type: "linear",
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: "rgba(25,118,210,0.25)" },
                            { offset: 1, color: "rgba(25,118,210,0.02)" },
                        ],
                    },
                },
                markArea: {
                    silent: true,
                    itemStyle: { color: "rgba(0,0,0,0.055)" },
                    label: { show: false },
                    data: weekendBands,
                },
            },
        ],
    };

    const renderContent = () => {
        if (error)
            return (
                <Typography variant="body1" color="error" p={2}>
                    Error loading data.
                </Typography>
            );

        if (isLoading)
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress size={28} />
                </Box>
            );

        if (dailyData.length === 0)
            return (
                <Typography variant="body2" color="text.secondary" p={2}>
                    No activity data available.
                </Typography>
            );

        return (
            <ReactEcharts
                option={option}
                notMerge={true}
                lazyUpdate={true}
                style={{ width: "100%", height: "260px" }}
            />
        );
    };

    const toggle = (
        <ToggleButtonGroup
            value={metric}
            exclusive
            onChange={(_, val) => val && setMetric(val)}
            size="small"
            sx={{ mr: 1 }}
        >
            <ToggleButton value="totalLoads" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                Page Loads
            </ToggleButton>
            <ToggleButton value="distinctDashboards" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                Distinct Dashboards
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Section
            title={"Activity Timeline"}
            isCollapsible={true}
            selectorsOrFilters={toggle}
        >
            {renderContent()}
        </Section>
    );
}
